import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { procApi } from '../../api/procurement.js';
import { wfApi } from '../../api/workflow.js';

export default function PurchaseOrders() {
  const { id } = useParams();
  if (id === 'new') return <NewPO />;
  if (id) return <PODetail id={id} />;
  return <POList />;
}

function POList() {
  const [items, setItems] = useState([]);
  useEffect(() => { procApi.listPOs().then((d) => setItems(d?.items || [])); }, []);
  return (
    <section>
      <header className="page-header"><h1>Purchase Orders</h1></header>
      <div className="card">
        <table className="data-table">
          <thead><tr><th>PO #</th><th>Vendor</th><th>Total</th><th>Expected</th><th>Status</th></tr></thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id}>
                <td><Link to={`/procurement/orders/${p.id}`}><code>{p.po_no}</code></Link></td>
                <td>{p.vendor_name}</td>
                <td>{p.currency} {Number(p.total).toFixed(2)}</td>
                <td>{p.expected_delivery || '—'}</td>
                <td><span className={`chip status-${chipStatus(p.status)}`}>{p.status}</span></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="muted">No POs yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function chipStatus(s) {
  if (['approved', 'received'].includes(s)) return 'approved';
  if (['rejected', 'cancelled'].includes(s)) return 'cancelled';
  if (s === 'draft' || s === 'pending_approval') return 'pending';
  if (s === 'partially_received' || s === 'closed') return 'in_progress';
  return 'pending';
}

function NewPO() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const rfqIdParam = params.get('rfq');
  const vendorIdParam = params.get('vendor');

  const [vendors, setVendors] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [rfqDetail, setRfqDetail] = useState(null);
  const [vendorId, setVendorId] = useState(vendorIdParam || '');
  const [warehouseId, setWarehouseId] = useState('');
  const [expected, setExpected] = useState(new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10));
  const [items, setItems] = useState([{ description: '', quantity: 1, unit: 'EA', unitPrice: 0, taxRate: 0 }]);
  const [products, setProducts] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch((import.meta.env.VITE_API_BASE_URL || '/api') + '/vendors?active=true', {
      headers: { Authorization: `Bearer ${localStorage.getItem('bos.access')}` },
    }).then((r) => r.json()).then((j) => setVendors(j?.data?.items || []));
    procApi.warehouses().then((d) => setWarehouses(d?.items || []));
    procApi.products({ active: true }).then((d) => setProducts(d?.items || []));
  }, []);

  // Auto-populate from a winning RFQ response.
  useEffect(() => {
    if (!rfqIdParam) return;
    procApi.getRFQ(rfqIdParam).then((d) => {
      setRfqDetail(d?.rfq);
      const vendor = d?.rfq?.vendors?.find((v) => v.vendor_id === vendorIdParam) || d?.rfq?.vendors?.[0];
      if (vendor) {
        setVendorId(vendor.vendor_id);
        const lines = typeof vendor.response_items === 'string' ? JSON.parse(vendor.response_items) : vendor.response_items;
        if (lines?.length) {
          setItems(lines.map((l) => ({
            description: l.description,
            quantity: Number(l.quantity || 1),
            unit: 'EA',
            unitPrice: Number(l.unitPrice || 0),
            taxRate: 0,
          })));
        }
      }
    });
  }, [rfqIdParam, vendorIdParam]);

  const totals = useMemo(() => {
    let subtotal = 0, tax = 0;
    for (const i of items) {
      const sub = Number(i.quantity || 0) * Number(i.unitPrice || 0);
      subtotal += sub;
      tax += sub * Number(i.taxRate || 0) / 100;
    }
    return { subtotal: round2(subtotal), tax: round2(tax), total: round2(subtotal + tax) };
  }, [items]);

  function setLine(i, k, v) { setItems((ls) => ls.map((l, j) => (j === i ? { ...l, [k]: v } : l))); }
  function addLine() { setItems((ls) => [...ls, { description: '', quantity: 1, unit: 'EA', unitPrice: 0, taxRate: 0 }]); }
  function removeLine(i) { setItems((ls) => ls.filter((_, j) => j !== i)); }

  async function submit(e) {
    e.preventDefault();
    setError(null);
    try {
      const payload = {
        vendorId,
        rfqId: rfqIdParam || null,
        warehouseId: warehouseId || null,
        expectedDelivery: expected || null,
        items: items.map((it) => ({
          productId: it.productId || null,
          description: it.description,
          quantity: Number(it.quantity),
          unit: it.unit,
          unitPrice: Number(it.unitPrice),
          taxRate: Number(it.taxRate || 0),
        })),
      };
      const created = await procApi.createPO(payload);
      nav(`/procurement/orders/${created.po.id}`);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed');
    }
  }

  return (
    <section>
      <header className="page-header"><h1>New Purchase Order</h1>{rfqDetail && <p className="muted">From RFQ {rfqDetail.rfq_no}</p>}</header>
      {error && <div className="alert error">{error}</div>}
      <form onSubmit={submit} className="card form-card">
        <div className="grid-form">
          <label>Vendor
            <select value={vendorId} onChange={(e) => setVendorId(e.target.value)} required>
              <option value="">Select…</option>
              {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          </label>
          <label>Warehouse
            <select value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}>
              <option value="">—</option>
              {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
          </label>
          <label className="full">Expected Delivery<input type="date" value={expected} onChange={(e) => setExpected(e.target.value)} /></label>
        </div>

        <table className="data-table je-grid">
          <thead><tr><th>Description</th><th>Product</th><th>Qty</th><th>Unit</th><th>Unit Price</th><th>Tax %</th><th>Total</th><th></th></tr></thead>
          <tbody>
            {items.map((l, i) => (
              <tr key={i}>
                <td><input value={l.description} onChange={(e) => setLine(i, 'description', e.target.value)} required /></td>
                <td>
                  <select value={l.productId || ''} onChange={(e) => setLine(i, 'productId', e.target.value || null)}>
                    <option value="">—</option>
                    {products.map((p) => <option key={p.id} value={p.id}>{p.sku} — {p.name}</option>)}
                  </select>
                </td>
                <td><input type="number" step="0.01" value={l.quantity} onChange={(e) => setLine(i, 'quantity', e.target.value)} /></td>
                <td><input value={l.unit} onChange={(e) => setLine(i, 'unit', e.target.value)} /></td>
                <td><input type="number" step="0.01" value={l.unitPrice} onChange={(e) => setLine(i, 'unitPrice', e.target.value)} /></td>
                <td><input type="number" step="0.01" value={l.taxRate} onChange={(e) => setLine(i, 'taxRate', e.target.value)} /></td>
                <td className="num">{(Number(l.quantity) * Number(l.unitPrice) * (1 + Number(l.taxRate || 0) / 100)).toFixed(2)}</td>
                <td>{items.length > 1 && <button type="button" className="btn ghost small" onClick={() => removeLine(i)}>×</button>}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr><td><button type="button" className="btn ghost small" onClick={addLine}>+ Add line</button></td><td colSpan={5} className="num">Subtotal</td><td className="num">{totals.subtotal.toFixed(2)}</td><td></td></tr>
            <tr><td></td><td colSpan={5} className="num">Tax</td><td className="num">{totals.tax.toFixed(2)}</td><td></td></tr>
            <tr><td></td><td colSpan={5} className="num"><strong>Total</strong></td><td className="num"><strong>{totals.total.toFixed(2)}</strong></td><td></td></tr>
          </tfoot>
        </table>

        <div className="actions">
          <button type="submit" className="btn primary">Create PO</button>
        </div>
      </form>
    </section>
  );
}

function round2(n) { return Math.round(Number(n) * 100) / 100; }

function PODetail({ id }) {
  const [po, setPo] = useState(null);
  const [instance, setInstance] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => { procApi.getPO(id).then((d) => setPo(d?.po)).catch((e) => setError(e.response?.data?.message)); }, [id]);
  useEffect(() => {
    if (po?.workflow_instance_id) wfApi.getInstance(po.workflow_instance_id).then((d) => setInstance(d?.instance)).catch(() => {});
  }, [po?.workflow_instance_id]);

  if (error) return <div className="alert error">{error}</div>;
  if (!po) return <div className="muted">Loading…</div>;
  const items = typeof po.items === 'string' ? JSON.parse(po.items) : po.items || [];

  return (
    <section>
      <header className="page-header">
        <div>
          <h1>{po.po_no}</h1>
          <p className="muted">{po.vendor_name} · {po.currency} {Number(po.total).toFixed(2)} · <span className={`chip status-${chipStatus(po.status)}`}>{po.status}</span></p>
        </div>
        <div className="actions">
          <Link to="/procurement/orders" className="btn ghost">← All POs</Link>
          {(po.status === 'approved' || po.status === 'partially_received') && (
            <Link to={`/procurement/receipts/new?po=${po.id}`} className="btn primary">Record Receipt</Link>
          )}
        </div>
      </header>

      <div className="card">
        <h3>Items</h3>
        <table className="data-table">
          <thead><tr><th>Description</th><th>Qty</th><th>Received</th><th>Unit Price</th><th>Line Total</th></tr></thead>
          <tbody>
            {items.map((it, i) => (
              <tr key={i}>
                <td>{it.description}</td>
                <td>{it.quantity}</td>
                <td>{Number(it.qtyReceived || 0)} {Number(it.qtyReceived || 0) >= Number(it.quantity || 0) && '✓'}</td>
                <td>{Number(it.unitPrice).toFixed(2)}</td>
                <td>{Number(it.total).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {instance && (
        <div className="card">
          <h3>Approval Progress</h3>
          <ol className="timeline">
            {instance.steps.map((s) => (
              <li key={s.id} className={`tl-${s.status}`}>
                <div className="tl-dot" />
                <div className="tl-content">
                  <strong>{s.step_name}</strong>
                  <span className={`chip status-${s.status === 'pending' ? 'pending' : s.status}`}>{s.status}</span>
                  {s.assignee_name && <span className="muted small">→ {s.assignee_name}</span>}
                  {s.acted_at && <div className="muted small">{new Date(s.acted_at).toLocaleString()}</div>}
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}
    </section>
  );
}
